/* eslint-disable camelcase */
import dayjs from 'dayjs';
import { formatNumber } from 'shared/utils/formatNumber';

const fmt = 'YYYY-MM-DD HH:mm:ssZ';

const toNumber = (v, fallback = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

export const getDiffByPercent = ({
  current,
  previous,
  fractionDigits = 2,
  epsilon = 1e-6,
  zeroPrevPolicy = 'signed100',
}) => {
  const c = toNumber(current, 0);
  const p = toNumber(previous, 0);

  if (Math.abs(p) < epsilon) {
    if (Math.abs(c) < epsilon) return 0;
    if (zeroPrevPolicy === 'zero') return 0;
    if (zeroPrevPolicy === 'dash') return 0;
    return c > 0 ? 100 : -100;
  }

  const res = ((c - p) / Math.abs(p)) * 100;
  if (!Number.isFinite(res)) return 0;

  return Number(res.toFixed(fractionDigits));
};

const normalizeDateUTC = value => {
  if (!value) return null;
  const d = dayjs.utc(value);
  if (!d.isValid()) return null;
  return d.startOf('day').format(fmt);
};

export const toFixedNumber = (v, fractionDigits = 2, fallback = 0) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return fallback;
  return Number(n.toFixed(fractionDigits));
};

export class MyStatisticsApiConverter {
  convertStatistic = data => {
    const currTotalRaw = data?.metric_statistic?.current_period?.total;
    const prevTotalRaw = data?.metric_statistic?.previous_period?.total;

    const currentTotal = toFixedNumber(currTotalRaw);
    const previousTotal = toFixedNumber(prevTotalRaw);

    return {
      balanceStatistic: {
        balanceOfUser: data.balance_statistic.balance_of_user,
        balanceOfCashier: data.balance_statistic.balance_of_cashier,
        balanceOfAdmin: data.balance_statistic.balance_of_admin,
      },
      totalStatistic: {
        usersCount: data.total_statistic.total_users_count,
        activeUsers: data.total_statistic.total_active_users_count,
        totalBets: data.total_statistic.total_bets,
        totalWins: data.total_statistic.total_wins,
        totalGGR: data.total_statistic.total_ggr,
        totalMargin: data.total_statistic.total_margin,
      },
      totalMetricStatistic: {
        metricName: data?.metric_statistic?.metric_name,
        totalDiff: Number(data?.metric_statistic?.previous_period?.total) || 0,
        diffPercent: getDiffByPercent({
          current: data?.metric_statistic?.current_period?.total,
          previous: data?.metric_statistic?.previous_period?.total,
        }),
        currentPeriod: {
          total: currentTotal,
          totalByDate: data?.metric_statistic?.current_period?.total_by_date?.map(t => ({
            date: normalizeDateUTC(t.date),
            count: Number(t.value).toFixed(2),
          })),
        },
        previousPeriod: {
          total: previousTotal,
          totalByDate: data?.metric_statistic?.previous_period?.total_by_date?.map(t => ({
            date: normalizeDateUTC(t.date),
            count: Number(t.value).toFixed(2),
          })),
        },
      },
      ggrRanking: data.ggr_rank_statistic.map((t, i) => ({
        number: i + 1,
        rank: t.rank,
        id: t.user_id,
        username: t.login,
        ggr: t.ggr,
      })),
    };
  };


  convertBalanceStatistic = data => {
    return data.items.map(item => ({
      rank: item.rank,
      id: item.user_id,
      username: item.nickname,
      actualBalance: formatNumber(item.actual_balance),
      startBalance: formatNumber(item.start_balance),
      endBalance: formatNumber(item.end_balance),
      changeBalance: item.change_balance,

      currentAgent: item.current_agent,
      directAgent: item.direct_agent,
    }));
  };
}

export default MyStatisticsApiConverter;
